import {
  Link,
  Toast,
  ToastBody,
  ToastPosition,
  ToastTitle,
  ToastTrigger,
  useId,
  useToastController,
} from "@fluentui/react-components";
import { FeedbackDetails, FeedbackInputs } from "./types";
import React from "react";

export function convertName(firstName: string, lastName: string): string {
  const firstInitial = firstName.slice(0, 1);
  const lastFiveLetters = lastName.slice(0, 5);
  const result = (firstInitial + lastFiveLetters).toUpperCase();
  return result;
}

export function extractInitialsFromName(name: string): string {
  const [firstName, lastName] = name.split(" ", 2); // Split by the first space
  const initials = (firstName[0] + (lastName ? lastName[0] : "")).toUpperCase(); // Get the first letters and concatenate
  return initials;
}

export function handleSwitch() {}

export function ensureError(value: unknown): Error {
  if (value instanceof Error) return value;

  if (value instanceof Event) return new Error("Event based error.");

  let stringify = "[Unable to stringify the thrown value]";
  try {
    stringify = JSON.stringify(value);
  } catch (error) {
    console.error("Error stringifying the value: ", error);
  }

  return new Error(`This value was thrown as is, constructed to an Error: ${stringify}`);
}

export async function reportError(e: Error, s: string) {
  try {
    await fetch("https://kafka-legal-analyst-be-stg.pwc.fi/log_error", {
      method: "POST",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "POST",
        Authorization: `Bearer ${s}`,
      },
      body: JSON.stringify({ error: e.message }),
    });
  } catch (error) {
    console.error("Error reporting the error: ", error);
  }
}

export async function postFeedback(feedback: FeedbackDetails | FeedbackInputs, token: string) {
  try {
    console.log("sending: ", JSON.stringify(feedback));
    const res = await fetch("https://kafka-legal-analyst-be-stg.pwc.fi/send_feedback", {
      method: "POST",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "POST",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(feedback),
    });
    if (res.status !== 200) throw new Error(`Feedback post returned status ${res.status}`);
    return await res.json();
  } catch (error: unknown) {
    const causeError = ensureError(error);
    const reportingError = new Error(
      `FeedbackPost.Cause${causeError.message
        .replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase())
        .replace(/\s/g, "")}`
    );
    reportError(reportingError, token);
    return { error: "Connection error. Feedback not posted." };
  }
}

type DispatchToastFn = (toastElement: React.ReactNode, options?: { position?: ToastPosition; intent?: string }) => void;
export function informUser(message: string, dispatchToast: DispatchToastFn, position: ToastPosition) {
  dispatchToast(
    <Toast appearance="inverted">
      <ToastTitle
        action={
          <ToastTrigger>
            <Link>Dismiss</Link>
          </ToastTrigger>
        }
      >
        Warning:
      </ToastTitle>
      <ToastBody>{message}</ToastBody>
    </Toast>,
    { position, intent: "warning" }
  );
}
export const taxLawMapping = {
  Arvonlisäverotus: [
    "Arvonlisäverolaki (1501/1993) (AVL)",
    "L Ahvenanmaan mk. koskevista poikkeuksista arvonlisävero- ja valmisteverolainsäädäntöön (1266/1996)",
    "Neuvoston direktiivi yhteisestä arvonlisäverojärjestelmästä (2006/112/EY) (AVLDir)",
  ],
  Autoverotus: ["Ajoneuvoverolaki (1281/2003)", "Autoverolaki (777/2020)", "Autoverolaki (kumottu) (1482/1994)"],
  Elinkeinoverotus: [
    "Laki elinkeinotulon verottamisesta (360/1968) (EVL, Elinkeinoverolaki)",
    "Laki avoimesta yhtiöstä ja kommandiittiyhtiöstä (389/1988) (AKL)",
    "Investointivarauslaki (1094/1978) InvVarL",
    "Laki kehitysalueelle tehtävien investointien korotetuista poistoista (1262/1993)",
    "Laki konserniavustuksesta verotuksessa (825/1986) (Konserniavustuslaki)",
    "Laki ulkomaisten väliyhteisöjen osakkaiden verotuksesta (1217/1994) (Väliyhteisölaki)",
    "Laki varojen arvostamisesta verotuksessa (1142/2005) (Arvostamislaki)",
    "Osakeyhtiölaki (624/2006) (OYL)",
    "Tuloverolaki (1535/1992) (TVL)",
    "Laki eräiden yleishyödyllisten yhteisöjen veronhuojennuksista (680/1976) (Verohuojennuslaki)",
    "Asunto-osakeyhtiölaki (1599/2009) (AOYL)",
    "Varainsiirtoverolaki (931/1996) (VSVL)",
  ],
  Ennakkoperintä: ["Ennakkoperintälaki (1118/1996) (EPL)", "Laki työnantajan sairausvakuutusmaksusta (771/2016)"],
  "Henkilöasiakkaan tuloverotus": [
    "Tuloverolaki (1535/1992) (TVL)",
    "Laki korkotulon lähdeverosta (1341/1990) (KlähdeVL)",
    "Laki rajoitetusti verovelvollisen tulon verottamisesta (627/1978) (Lähdeverolaki)",
    "Laki ulkomailta tulevan palkansaajan lähdeverosta (1551/1995)",
    "Tuloa ja varallisuutta koskeva malliverosopimus (SopS) OECD, Tuloverosopimus",
    "Maatilatalouden tuloverolaki (543/1967) (MTL, MVL)",
    "Laki varojen arvostamisesta verotuksessa (1142/2005) (Arvostamislaki)",
    "Varallisuusverolaki (30.12.1992/1537)",
  ],
  Kiinteistöverotus: [
    "Kiinteistöverolaki (654/1992) (KVL)",
    "Asunto-osakeyhtiölaki (1599/2009) (AOYL)",
    "Varainsiirtoverolaki (931/1996) (VSVL)",
  ],
  Maatalousverotus: [
    "Maatilatalouden tuloverolaki (543/1967) (MTL, MVL)",
    "Laki maataloudessa käytettyjen eräiden energiatuotteiden valmisteveron palautuksesta (603/2006)",
  ],
  Metsäverotus: ["Tuloverolaki (1535/1992) (TVL)"],
  "Perintö- ja lahjaverotus": ["Perintö- ja lahjaverolaki (378/1940) (PVL, Perintöverolaki, PerVL)"],
  Tulli: [
    "Tullilaki (304/2016)",
    "Laki erillisellä päätöksellä määrättävästä veron- tai tullinkorotuksesta (781/2013)",
  ],
  Vakuutusmaksuverotus: ["Laki eräistä vakuutusmaksuista suoritettavasta verosta (664/1966) (VakmVL)"],
  Valmisteverotus: [
    "Valmisteverotuslaki (182/2010) (ValmVL)",
    "Valmisteverotuslaki (pääosin kumottu) (1469/1994) (Vanha ValmVL)",
    "Jäteverolaki (1126/2010)",
    "Laki alkoholi- ja alkoholijuomaverosta (1471/1994) (Alkoholiverolaki)",
    "Laki eräiden juomapakkausten valmisteverosta (1037/2004)",
    "Laki maataloudessa käytettyjen eräiden energiatuotteiden valmisteveron palautuksesta (603/2006)",
    "Laki nestemäisten polttoaineiden valmisteverosta (1472/1994) (Polttoaineverolaki)",
    "Laki sähkön ja eräiden polttoaineiden valmisteverosta (1260/1996) (Energiaverolaki, Sähköverolaki)",
    "Laki tupakkaverosta (1470/1994) (Tupakkaverolaki)",
    "Laki virvoitusjuomaverosta (1127/2010) (Makeisverolaki)",
    "L Ahvenanmaan mk. koskevista poikkeuksista arvonlisävero- ja valmisteverolainsäädäntöön (1266/1996)",
  ],
};
